/**
 * Compares two arrays for shallow equality
 * @param {number[]} arr1 - First array to compare
 * @param {number[]} arr2 - Second array to compare
 * @returns {boolean} True if arrays are equal
 */
const shallowEqual = (arr1, arr2) =>
	arr1?.length === arr2?.length && arr1.every((val, i) => val === arr2[i]);

/**
 * RangeGroup - A vanilla web component for multi-thumb range sliders
 * Supports multiple range inputs with custom styling and interactions
 */
export class RangeGroup extends HTMLElement {
	// Private fields
	#values = [];
	#previousInputValues = [];
	#previousChangeValues = [];
	#datalistOptions = [];
	#inputs = [];
	#thumbs = [];
	#legendElements = [];
	#activeThumbIndex = null;
	#containerRect = null;
	#pendingActivation = null;
	#uniqueId = Math.random().toString(36).substring(2, 9);
	#shadowRoot;
	#container = null;
	#track = null;
	#legendSlot = null;
	#inputSlot = null;

	// Public properties
	min = 0;
	max = 100;
	stepBetween = 0;
	list = "";

	/**
	 * Formatter function for value text
	 * @type {(value: number) => string}
	 */
	valueTextFormatter = (value) => String(Math.round(value));

	static get observedAttributes() {
		return ["min", "max", "stepbetween", "list"];
	}

	constructor() {
		super();
		this.#shadowRoot = this.attachShadow({ mode: "open" });
		this.#render();
	}

	/**
	 * Public API: Get current values
	 * @returns {number[]}
	 */
	get values() {
		return this.#values;
	}

	/**
	 * Public API: Get input elements
	 * @returns {HTMLInputElement[]}
	 */
	get inputs() {
		return this.#inputs;
	}

	/**
	 * Get a specific range input by index
	 * @param {number} index - The index of the input
	 * @returns {HTMLInputElement | undefined} The input element
	 */
	getRangeInput(index) {
		return this.#inputs[index];
	}

	/**
	 * Set a specific range value
	 * @param {number} index - The index of the input
	 * @param {number} value - The new value
	 */
	setRangeValue(index, value) {
		if (this.#inputs[index]) {
			this.#inputs[index].value = String(this.#normalizeValue(value, index));
			this.#updateValues();
		}
	}

	connectedCallback() {
		// Global listeners are now added only during active drag (see #handleContainerPointerDown)
	}

	disconnectedCallback() {
		// Clean up in case component is removed during drag
		window.removeEventListener("pointermove", this.#handlePointerMove);
		window.removeEventListener("pointerup", this.#handlePointerUp);
	}

	/**
	 * Handle attribute changes
	 * @param {string} name - Attribute name
	 * @param {string | null} oldValue - Old value
	 * @param {string | null} newValue - New value
	 */
	attributeChangedCallback(name, oldValue, newValue) {
		if (oldValue === newValue) return;

		switch (name) {
			case "min":
				this.min = Number(newValue) || 0;
				break;
			case "max":
				this.max = Number(newValue) || 100;
				break;
			case "stepbetween":
				this.stepBetween = Number(newValue) || 0;
				break;
			case "list":
				this.list = newValue || "";
				this.#parseDatalist();
				this.#updateUI();
				break;
		}
	}

	/**
	 * Initial render of the component's shadow DOM
	 */
	#render() {
		this.#shadowRoot.innerHTML = `
			<style>
				:host {
					display: block;
					position: relative;
					--_thumb-size: var(--thumb-size, 24px);
					--_track-height: var(--track-height, 6px);
					--thumb-bg: currentColor;
					padding-block: calc(var(--_thumb-size) / 2);
				}

				.wrapper {
					border: 0;
					padding: 0;
					margin: 0;
				}

				.container {
					position: relative;
					width: 100%;
					height: var(--_thumb-size);
				}

				.track {
					position: absolute;
					top: 50%;
					left: 0;
					width: 100%;
					height: var(--_track-height);
					transform: translateY(-50%);
					background-color: #ccc;
				}

				.segment {
					position: absolute;
					top: 0;
					height: 100%;
					background-color: #999;
				}

				.thumb {
					position: absolute;
					top: 50%;
					width: var(--_thumb-size);
					height: var(--_thumb-size);
					background-color: var(--thumb-bg);
					border-radius: 50%;
					transform: translate(-50%, -50%);
					cursor: pointer;
					touch-action: none;
					box-shadow: 0 0 8px 0 oklch(from var(--thumb-bg) l c h / 0.3);
					transition: transform 0.1s ease-in-out;
					border: none;
					padding: 0;
				}

				.thumb::before {
					content: "";
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%);
					width: calc(var(--_thumb-size) + 20px);
					height: calc(var(--_thumb-size) + 20px);
					background: transparent;
					border-radius: 50%;
				}

				.thumb:hover {
					transform: translate(-50%, -50%) scale(1.1);
				}

				.thumb:focus-visible {
					outline: 2px solid var(--thumb-bg);
					outline-offset: 4px;
				}

				.ticks-wrapper {
					position: absolute;
					top: calc(50% + var(--_track-height) / 2 + 4px);
					left: 0;
					right: 0;
					height: 20px;
					pointer-events: none;
				}

				.tick-marks,
				.tick-labels {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
				}

				.tick {
					position: absolute;
					transform: translateX(-50%);
					width: 1px;
					height: 6px;
					background: var(--tick-color, currentColor);
				}

				.tick-label {
					position: absolute;
					top: 10px;
					transform: translateX(-50%);
					font-size: 0.75rem;
					color: var(--tick-label-color, currentColor);
				}
			</style>
			<fieldset class="wrapper">
				<slot name="legend"></slot>
				<div class="container" role="group">
					<div part="slider-track" class="track"></div>
				</div>
				<slot style="display: none;"></slot>
			</fieldset>
		`;

		// Cache DOM references
		this.#container = this.#shadowRoot.querySelector(".container");
		this.#track = this.#shadowRoot.querySelector(".track");
		this.#legendSlot = this.#shadowRoot.querySelector('slot[name="legend"]');
		this.#inputSlot = this.#shadowRoot.querySelector("slot:not([name])");

		// Set up event listeners
		if (this.#container) {
			this.#container.addEventListener("pointerdown", this.#handleContainerPointerDown.bind(this));
		}

		if (this.#legendSlot) {
			this.#legendSlot.addEventListener("slotchange", this.#onSlotChange.bind(this));
		}

		if (this.#inputSlot) {
			this.#inputSlot.addEventListener("slotchange", this.#onSlotChange.bind(this));
		}
	}

	/**
	 * Handle slot changes to detect new input elements
	 */
	#onSlotChange() {
		if (this.#inputSlot) {
			const assignedElements = this.#inputSlot.assignedElements({
				flatten: true,
			});
			this.#inputs = assignedElements.flatMap((el) =>
				el.matches('input[type="range"]')
					? [el]
					: Array.from(el.querySelectorAll('input[type="range"]')),
			);
		}

		if (this.#legendSlot) {
			const legendElements = this.#legendSlot.assignedElements({
				flatten: true,
			});
			this.#legendElements = legendElements.filter((el) => el instanceof HTMLLegendElement);
		}

		this.#initializeInputs();
	}

	/**
	 * Get accessible name for a range input
	 * @param {HTMLInputElement} input - The input element
	 * @param {number} index - The index of the input
	 * @returns {string} The accessible name
	 */
	#getAccessibleName(input, index) {
		let controlLabel = null;
		if (input) {
			// 1. aria-labelledby
			const labelledby = input.getAttribute("aria-labelledby");
			if (labelledby) {
				const labelElement = document.getElementById(labelledby);
				controlLabel = labelElement?.textContent?.trim() || null;
			}

			// 2. aria-label
			if (!controlLabel) {
				controlLabel = input.getAttribute("aria-label");
			}

			// 3. Associated <label> element
			if (!controlLabel && input.labels && input.labels.length > 0) {
				controlLabel = input.labels[0].textContent?.trim() || null;
			}

			// 4. Fallback to name attribute
			if (!controlLabel) {
				controlLabel = input.name;
			}
		}

		// 5. Final fallback
		const finalControlLabel = controlLabel || `value ${index + 1}`;

		const legendText = this.#legendElements?.[0]?.textContent?.trim();

		if (legendText) {
			return `${legendText}, ${finalControlLabel}`;
		}

		return finalControlLabel;
	}

	/**
	 * Initialize input elements with default values
	 */
	#initializeInputs() {
		if (this.#inputs.length === 0) return;

		this.#inputs.sort((a, b) => Number(a.getAttribute("value")) - Number(b.getAttribute("value")));

		this.#inputs.forEach((input) => {
			if (!input.hasAttribute("min") && this.hasAttribute("min")) {
				input.min = String(this.min);
			}
			if (!input.hasAttribute("max") && this.hasAttribute("max")) {
				input.max = String(this.max);
			}
			const initialValue = input.getAttribute("value");
			if (initialValue !== null) {
				input.value = initialValue;
			}
		});

		this.#updateValues();
		this.#updateUI();
	}

	/**
	 * Parse datalist options from the DOM
	 */
	#parseDatalist() {
		if (!this.list) {
			this.#datalistOptions = [];
			return;
		}
		const datalist = document.getElementById(this.list);
		if (datalist instanceof HTMLDataListElement) {
			this.#datalistOptions = Array.from(datalist.options).map((opt) => ({
				value: opt.value,
				label: opt.label || opt.value,
			}));
		}
	}

	/**
	 * Update internal values array from input elements
	 */
	#updateValues() {
		const newValues = this.#inputs.map((input, index) =>
			this.#normalizeValue(Number(input.value), index),
		);
		if (!shallowEqual(this.#values, newValues)) {
			this.#values = newValues;
			this.#updateUI();
		}
	}

	/**
	 * Dispatch input or change events
	 * @param {string} name - Event name ('input' or 'change')
	 */
	#dispatch(name = "input") {
		let shouldDispatch = true;
		if (name === "change") {
			shouldDispatch = !shallowEqual(this.#values, this.#previousChangeValues);
			if (shouldDispatch) this.#previousChangeValues = [...this.#values];
		} else {
			shouldDispatch = !shallowEqual(this.#values, this.#previousInputValues);
			if (shouldDispatch) this.#previousInputValues = [...this.#values];
		}
		if (shouldDispatch) {
			this.dispatchEvent(new Event(name, { bubbles: true, composed: true }));
		}
	}

	/**
	 * Handle pointer down on container (track or thumb)
	 * @param {PointerEvent} e - The pointer event
	 */
	#handleContainerPointerDown(e) {
		if (e.button !== 0) return;

		this.#containerRect = this.#container?.getBoundingClientRect() ?? null;
		if (!this.#containerRect) return;

		const target = e.target;
		const isThumb = target.classList.contains("thumb");

		if (isThumb) {
			// Thumb click: start a drag operation
			const thumbIndex = Array.from(this.#thumbs).indexOf(target);
			if (thumbIndex === -1) return;

			const currentValue = this.#values[thumbIndex];
			const overlappingIndices = this.#values.reduce((acc, v, i) => {
				if (Math.abs(v - currentValue) < 0.001) acc.push(i);
				return acc;
			}, []);

			if (overlappingIndices.length > 1) {
				this.#pendingActivation = {
					indices: overlappingIndices,
					initialX: e.clientX,
				};
				this.#activeThumbIndex = null;
			} else {
				this.#activeThumbIndex = thumbIndex;
				this.#thumbs[thumbIndex]?.focus();
			}

			// Add global listeners only during drag
			window.addEventListener("pointermove", this.#handlePointerMove);
			window.addEventListener("pointerup", this.#handlePointerUp);

			target.setPointerCapture(e.pointerId);
		} else {
			// Track click: move the closest thumb
			const percent = Math.max(
				0,
				Math.min(1, (e.clientX - this.#containerRect.left) / this.#containerRect.width),
			);
			const value = this.min + percent * (this.max - this.min);

			if (this.#values.length === 0) return;

			const thumbIndex = this.#values.reduce((closestIndex, currentValue, currentIndex) => {
				const closestDistance = Math.abs(this.#values[closestIndex] - value);
				const currentDistance = Math.abs(currentValue - value);
				return currentDistance < closestDistance ? currentIndex : closestIndex;
			}, 0);

			this.setRangeValue(thumbIndex, value);
			this.#dispatch("input");
			this.#dispatch("change");
		}
	}

	/**
	 * Handle pointer move (dragging thumb)
	 * @param {PointerEvent} e - The pointer event
	 */
	#handlePointerMove = (e) => {
		if (this.#pendingActivation) {
			const dx = e.clientX - this.#pendingActivation.initialX;
			if (Math.abs(dx) > 2) {
				const direction = dx > 0 ? "right" : "left";
				const indices = this.#pendingActivation.indices;
				this.#activeThumbIndex = direction === "left" ? Math.min(...indices) : Math.max(...indices);
				this.#thumbs[this.#activeThumbIndex]?.focus();
				this.#pendingActivation = null;
			}
		}

		if (this.#activeThumbIndex === null || !this.#containerRect) return;
		e.preventDefault();

		const percent = Math.max(
			0,
			Math.min(1, (e.clientX - this.#containerRect.left) / this.#containerRect.width),
		);
		const value = this.min + percent * (this.max - this.min);

		this.setRangeValue(this.#activeThumbIndex, value);
		this.#dispatch("input");
	};

	/**
	 * Handle pointer up (end drag)
	 */
	#handlePointerUp = () => {
		if (this.#activeThumbIndex !== null || this.#pendingActivation !== null) {
			this.#dispatch("change");
		}
		this.#activeThumbIndex = null;
		this.#pendingActivation = null;

		// Remove global listeners when drag ends
		window.removeEventListener("pointermove", this.#handlePointerMove);
		window.removeEventListener("pointerup", this.#handlePointerUp);
	};

	/**
	 * Handle keyboard navigation
	 * @param {KeyboardEvent} e - The keyboard event
	 * @param {number} index - The thumb index
	 */
	#handleKeyDown(e, index) {
		const input = this.#inputs[index];
		if (!input) return;

		const step = Number(input.step) || 1;
		let newValue = Number(input.value);

		if (this.list && this.#datalistOptions.length > 0) {
			const sortedValues = this.#datalistOptions
				.map((opt) => Number(opt.value))
				.slice()
				.sort((a, b) => a - b);
			const currentIndex = sortedValues.indexOf(newValue);
			if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
				newValue = sortedValues[Math.max(0, currentIndex - 1)];
			} else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
				newValue = sortedValues[Math.min(sortedValues.length - 1, currentIndex + 1)];
			}
		} else {
			if (e.key === "ArrowLeft" || e.key === "ArrowDown") newValue -= step;
			else if (e.key === "ArrowRight" || e.key === "ArrowUp") newValue += step;
		}

		if (e.key === "Home") newValue = this.min;
		else if (e.key === "End") newValue = this.max;
		else if (!["ArrowLeft", "ArrowDown", "ArrowRight", "ArrowUp"].includes(e.key)) return;

		e.preventDefault();
		this.setRangeValue(index, newValue);
		this.#dispatch("input");
		this.#dispatch("change");
	}

	/**
	 * Snap value to nearest datalist option
	 * @param {number} value - The value to snap
	 * @returns {number} The snapped value
	 */
	#snapToDataList(value) {
		if (!this.list || this.#datalistOptions.length === 0) return value;
		const validValues = this.#datalistOptions.map((opt) => Number(opt.value));
		return validValues.reduce((prev, curr) =>
			Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
		);
	}

	/**
	 * Normalize value based on constraints
	 * @param {number} value - The value to normalize
	 * @param {number} index - The thumb index
	 * @returns {number} The normalized value
	 */
	#normalizeValue(value, index) {
		const input = this.#inputs[index];
		if (!input) return value;
		let finalValue = value;
		if (this.list && this.#datalistOptions.length > 0) {
			finalValue = this.#snapToDataList(finalValue);
		}
		const step = this.stepBetween || 0;
		const prevInput = this.#inputs[index - 1];
		if (prevInput) {
			const minAllowed = Number(prevInput.value) + step;
			if (finalValue < minAllowed) {
				if (this.list && this.#datalistOptions.length > 0) {
					const sortedValues = this.#datalistOptions
						.map((opt) => Number(opt.value))
						.slice()
						.sort((a, b) => a - b);
					finalValue = sortedValues.find((v) => v >= minAllowed) ?? finalValue;
				} else {
					finalValue = minAllowed;
				}
			}
		}
		const nextInput = this.#inputs[index + 1];
		if (nextInput) {
			const maxAllowed = Number(nextInput.value) - step;
			if (finalValue > maxAllowed) {
				if (this.list && this.#datalistOptions.length > 0) {
					const sortedValues = this.#datalistOptions
						.map((opt) => Number(opt.value))
						.slice()
						.sort((a, b) => a - b);
					finalValue =
						sortedValues
							.slice()
							.reverse()
							.find((v) => v <= maxAllowed) ?? finalValue;
				} else {
					finalValue = maxAllowed;
				}
			}
		}
		const inputMinAttr = input.getAttribute("min");
		const inputMaxAttr = input.getAttribute("max");
		const inputMin = inputMinAttr !== null ? Number(inputMinAttr) : this.min;
		const inputMax = inputMaxAttr !== null ? Number(inputMaxAttr) : this.max;
		const thumbMin = Math.max(this.min, inputMin);
		const thumbMax = Math.min(this.max, inputMax);
		return Math.max(thumbMin, Math.min(thumbMax, finalValue));
	}

	/**
	 * Convert value to percentage
	 * @param {number} value - The value to convert
	 * @returns {number} The percentage
	 */
	#valueToPercent(value) {
		if (this.max - this.min === 0) return 0;
		return ((value - this.min) / (this.max - this.min)) * 100;
	}

	/**
	 * Update the UI to reflect current state
	 */
	#updateUI() {
		if (!this.#track || !this.#container) return;

		// Update legend ID
		const legend = this.#legendElements?.[0];
		if (legend && !legend.id) {
			legend.id = `rg-legend-${this.#uniqueId}`;
		}
		const legendId = legend?.id;
		if (legendId && this.#container) {
			this.#container.setAttribute("aria-labelledby", legendId);
		}

		// Clear existing segments, ticks, and thumbs
		this.#track.innerHTML = "";
		const existingTicks = this.#container.querySelector(".ticks-wrapper");
		const existingThumbs = this.#container.querySelectorAll(".thumb");
		existingTicks?.remove();
		existingThumbs.forEach((thumb) => {
			thumb.remove();
		});

		// Render segments
		const segmentPoints = [0, ...this.#values.map((v) => this.#valueToPercent(v)), 100];
		segmentPoints.slice(0, -1).forEach((p, i) => {
			const left = p;
			const width = segmentPoints[i + 1] - p;
			const segment = document.createElement("div");
			segment.className = "segment";
			segment.setAttribute("part", `slider-segment slider-segment-${i + 1}`);
			segment.style.left = `${left}%`;
			segment.style.width = `${width}%`;
			this.#track.appendChild(segment);
		});

		// Render ticks if datalist exists
		if (this.#datalistOptions.length > 0) {
			const ticksWrapper = document.createElement("div");
			ticksWrapper.className = "ticks-wrapper";
			ticksWrapper.setAttribute("aria-hidden", "true");

			const tickMarks = document.createElement("div");
			tickMarks.className = "tick-marks";
			tickMarks.setAttribute("part", "slider-ticks");

			const tickLabels = document.createElement("div");
			tickLabels.className = "tick-labels";
			tickLabels.setAttribute("part", "slider-tick-labels");

			this.#datalistOptions.forEach((opt, index) => {
				const tick = document.createElement("div");
				tick.className = "tick";
				tick.setAttribute("part", `slider-tick slider-tick-${index + 1}`);
				tick.style.left = `${this.#valueToPercent(Number(opt.value))}%`;
				tickMarks.appendChild(tick);

				const tickLabel = document.createElement("div");
				tickLabel.className = "tick-label";
				tickLabel.setAttribute("part", `slider-tick-label slider-tick-label-${index + 1}`);
				tickLabel.style.left = `${this.#valueToPercent(Number(opt.value))}%`;
				tickLabel.textContent = opt.label;
				tickLabels.appendChild(tickLabel);
			});

			ticksWrapper.appendChild(tickMarks);
			ticksWrapper.appendChild(tickLabels);
			this.#container.appendChild(ticksWrapper);
		}

		// Render thumbs
		this.#thumbs = [];
		this.#values.forEach((value, index) => {
			const thumb = document.createElement("button");
			thumb.className = "thumb";
			thumb.setAttribute("part", `slider-thumb slider-thumb-${index + 1}`);
			thumb.style.left = `${this.#valueToPercent(value)}%`;
			thumb.style.zIndex = index === this.#activeThumbIndex ? "12" : "10";
			thumb.setAttribute("role", "slider");
			thumb.setAttribute("aria-label", this.#getAccessibleName(this.#inputs[index], index));
			thumb.setAttribute("aria-valuemin", String(this.min));
			thumb.setAttribute("aria-valuemax", String(this.max));
			thumb.setAttribute("aria-valuenow", String(Math.round(value)));
			thumb.setAttribute("aria-valuetext", this.valueTextFormatter(value));
			thumb.addEventListener("keydown", (e) => this.#handleKeyDown(e, index));

			this.#container.appendChild(thumb);
			this.#thumbs.push(thumb);
		});
	}
}

// Register the custom element
customElements.define("range-group", RangeGroup);
